import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";

// Anchors are written as absolute paths so they navigate correctly
// from any marketing page (e.g. /security back to landing sections).
const NAV_LINKS = [
  { label: "Features", to: "/#features" },
  { label: "How It Works", to: "/#how" },
  { label: "Scanner", to: "/#scanner" },
  { label: "Pricing", to: "/#pricing" },
  { label: "Security", to: "/security" },
];

export default function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Smooth-scroll to in-page anchors when already on the landing page.
  const handleNavClick = (to) => (e) => {
    if (to.startsWith("/#") && window.location.pathname === "/") {
      e.preventDefault();
      const id = to.slice(2);
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpen(false);
    }
  };

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 transition-all duration-200"
      style={{
        backdropFilter: scrolled ? "blur(12px)" : "none",
        background: scrolled ? "rgba(15,28,46,0.85)" : "transparent",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
      }}
      data-testid="marketing-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/"><Logo variant="light" /></Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={handleNavClick(l.to)}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/login" className="btn btn-ghost-on-dark" data-testid="nav-signin-button">Sign in</Link>
          <Link to="/signup" className="btn btn-primary" data-testid="nav-signup-button">Start free trial</Link>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-navy-900 border-t border-white/5 px-6 py-6 space-y-4">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={(e) => { handleNavClick(l.to)(e); setOpen(false); }} className="block text-white/80 t-body">{l.label}</Link>
          ))}
          <div className="pt-4 grid grid-cols-2 gap-3">
            <Link to="/login" className="btn btn-secondary-on-dark" onClick={() => setOpen(false)}>Sign in</Link>
            <Link to="/signup" className="btn btn-primary" onClick={() => setOpen(false)}>Free trial</Link>
          </div>
        </div>
      )}
    </header>
  );
}
